/**
 * FinancialModuleGuard - Proteção de rota para o módulo Financeiro
 * 
 * Verifica se o tenant atual possui o módulo Financeiro habilitado (financial_enabled).
 * Se não possuir, registra tentativa de bypass e redireciona para /tenant.
 * 
 * @version 1.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação do guard financeiro
 */

import { ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para auditoria de segurança do módulo Financeiro
const financialLogger = createLogger('FinancialModuleGuard');

interface FinancialModuleGuardProps {
  /** Conteúdo a ser renderizado se o módulo Financeiro estiver habilitado */
  children: ReactNode;
}

/**
 * Registra tentativa de acesso ao Financeiro sem módulo habilitado
 * na tabela security_bypass_attempts para auditoria
 */
const logFinancialBypassAttempt = async (
  userId: string | undefined,
  tenantId: string | undefined,
  route: string
): Promise<void> => {
  try {
    if (!userId) return;

    let fullName: string | null = null;
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle();
      fullName = profileData?.full_name || null;
    } catch {
      // Não bloquear se falhar busca do perfil
    }

    const { error } = await supabase
      .from('security_bypass_attempts')
      .insert({
        user_id: userId,
        attempted_tenant_id: tenantId || null,
        function_name: 'FinancialModuleGuard',
        action: 'financial_module_access_denied',
        attempted_value: route,
        user_agent: navigator.userAgent,
        details: {
          route,
          full_name: fullName,
          reason: 'tenant_financial_not_enabled',
          timestamp: new Date().toISOString(),
        },
      });

    if (error) {
      financialLogger.error('Falha ao registrar tentativa de bypass Financeiro', { error: error.message });
    } else {
      financialLogger.warn('Tentativa de acesso ao Financeiro sem módulo habilitado registrada', {
        route,
        tenantId,
      });
    }
  } catch (err) {
    financialLogger.error('Erro ao registrar bypass attempt Financeiro', { err });
  }
};

/**
 * Componente guard que bloqueia acesso às rotas Financeiro
 * quando o tenant atual não possui financial_enabled = true
 */
export function FinancialModuleGuard({ children }: FinancialModuleGuardProps) {
  const location = useLocation();
  const { user, currentTenant } = useAuth();

  // Ref para evitar múltiplos logs da mesma tentativa
  const hasLoggedBypass = useRef(false);

  // Verificar se o módulo Financeiro está habilitado no tenant atual
  const financialEnabled = currentTenant?.financial_enabled ?? false;

  // Registrar tentativa de bypass quando módulo não está habilitado
  useEffect(() => {
    if (!financialEnabled && !hasLoggedBypass.current && currentTenant) {
      hasLoggedBypass.current = true;
      logFinancialBypassAttempt(user?.id, currentTenant?.id, location.pathname);
    }
  }, [financialEnabled, user?.id, currentTenant, location.pathname]);

  // Se Financeiro não habilitado, redirecionar para dashboard principal
  if (!financialEnabled) {
    financialLogger.warn('Acesso ao Financeiro bloqueado: módulo não habilitado para o tenant', {
      tenantId: currentTenant?.id,
      route: location.pathname,
    });
    return <Navigate to="/tenant" replace />;
  }

  // Módulo Financeiro habilitado - renderizar conteúdo
  return <>{children}</>;
}
